/**
 * Shared fetch seam for the S2 hooks (CD-7: hooks fetch, kit renders).
 * Errors are typed values the pages can show honestly — an ApiError carries the
 * backend's structured body (ResolveRejection reason, auth detail, CD-9 500
 * envelope) so the grid never fails blank.
 */

import { getApiEndpoint } from '../config/env'

export interface ApiErrorBody {
  [key: string]: unknown
}

function apiErrorMessage(status: number, body: ApiErrorBody | null): string {
  if (body) {
    if (typeof body.reason === 'string') return body.reason // ResolveRejection (422)
    if (typeof body.detail === 'string') return body.detail // auth / CD-9 500 boundary
    if (typeof body.error === 'string') return `${body.error} (HTTP ${status})`
  }
  return `Request failed (HTTP ${status})`
}

export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody | null

  constructor(status: number, body: ApiErrorBody | null) {
    super(apiErrorMessage(status, body))
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/** GET a JSON API resource with the carried AuthContext bearer token. */
export async function fetchApi<T>(path: string, token: string | null): Promise<T> {
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  const response = await fetch(getApiEndpoint(path), { headers })
  if (!response.ok) {
    let body: ApiErrorBody | null = null
    try {
      body = (await response.json()) as ApiErrorBody
    } catch {
      body = null // non-JSON error body — the status alone tells the story
    }
    throw new ApiError(response.status, body)
  }
  return (await response.json()) as T
}
